import type { QueryClient } from '@tanstack/react-query'

/**
 * An answer that left before an invalidation does not count after it (#126,
 * 1.95.1).
 *
 * TanStack Query marks a query that nobody shows as invalid and fetches it
 * anew when it is shown again. But if a fetch for it is still on its way at
 * that moment, its answer lands afterwards, carries the state from before,
 * and **clears the mark** (`successState()` sets `isInvalidated: false`).
 * The page then shows the old state as fresh until its `staleTime` runs out.
 * Measured on the photo page: open it, go to the chat before its list has
 * come back, delete a post with pictures, go back -- the deleted pictures
 * stand there for 30 s (`bildentfernen.mjs`, "eine Antwort von vor dem
 * Löschen").
 *
 * A query that someone shows has no such gap: `invalidateQueries()` cancels
 * its fetch and starts a new one. So only the hidden ones are handled, and
 * here, once, for all 120 invalidations in the app -- at each call site it
 * would be missing at the next one.
 *
 * Cancelled with `revert`, the query goes back to the state from before the
 * fetch, and that state was not yet invalid. Hence the second `invalidate()`
 * afterwards; it fires this listener again, now with nothing in flight.
 */
export function dropAnswersFromBeforeInvalidation(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'invalidate') return
    const query = event.query
    if (query.state.fetchStatus !== 'fetching' || query.isActive()) return
    void query.cancel({ revert: true }).then(() => query.invalidate())
  })
}
